/**
 * O resultado de uma linha da conferência de identidade pelo painel (E203.F4, ADR-0146 §6): o
 * perito escolhe consistente, inconclusivo ou divergente, com fundamento; o eixo de identidade é
 * recalculado das linhas e o documento imprime só o resultado do perito.
 *
 * E203.F4.C (F-E203.F4-05): o formulário abre pelo resultado que o documento imprime na linha; sem
 * ele, nada vem escolhido e "Salvar" espera a escolha.
 *
 * E207.F7 (owner, 01/11): na linha do crédito o resultado vem em cima dos valores lidos, com o
 * texto do crédito: consistente numa divergência apurada grava o aceite com o mesmo fundamento.
 *
 * E211.F1.T05 (owner, 01/11): o Salvar fica sempre ligado e, ao clicar sem o resultado ou sem o
 * fundamento mínimo, diz o que falta (a mesma regra do painel do crédito, E207.F7.C01).
 */
import { useState } from 'react';

import { Field, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { RESULTADOS_DA_LINHA, vigenteEntre, type ResultadoDaLinha } from '@/lib/revisaoDoDado';
import { cn } from '@/lib/utils';

import { Fundamento, faltaNoFundamento } from './Fundamento';
import { BotaoSalvar, Falta, useGravacao } from './Gravacao';
import { gravarRevisao } from './gravarRevisao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.linha;
const falta = ptBR.painelDoDado.falta;

/** E211.F3: o eixo de geolocalização da página 1 usa o mesmo formulário, com o próprio texto. */
export function LinhaNoPainel(props: PropsDoConteudo & { texto?: string }) {
  const { pedido, acoes, onMudou } = props;
  const chave = pedido.origem.linha ?? '';
  const [resultado, setResultado] = useState<ResultadoDaLinha | ''>(() =>
    vigenteEntre(RESULTADOS_DA_LINHA, pedido.origem.valor),
  );
  const [fundamento, setFundamento] = useState('');
  const [oQueFalta, setOQueFalta] = useState<string | null>(null);
  const gravacao = useGravacao(
    () =>
      gravarRevisao(props, {
        alvo: 'linha',
        chave,
        valor: resultado,
        fundamento: fundamento.trim(),
      }),
    () => {
      acoes.salvo();
      onMudou();
    },
  );
  const doCredito = pedido.origem.tipo === 'credito';
  function salvar(): void {
    const pendente = resultado === '' ? falta.resultado : faltaNoFundamento(fundamento);
    setOQueFalta(pendente);
    if (pendente === null) void gravacao.salvar();
  }
  return (
    <div
      className={cn('space-y-sc-3', !doCredito && 'border-t border-border pt-sc-3')}
      data-testid="painel-linha"
      data-do-credito={doCredito ? 'true' : undefined}
    >
      <p className="text-body-sm text-fg-muted">
        {props.texto ?? (doCredito ? t.textoDoCredito : t.texto)}
      </p>
      <Field label={t.rotulo} htmlFor="painel-linha-resultado">
        <Select
          id="painel-linha-resultado"
          value={resultado}
          onChange={(e) => setResultado(vigenteEntre(RESULTADOS_DA_LINHA, e.target.value))}
          data-testid="painel-linha-resultado"
        >
          <option value="" disabled>
            {t.escolha}
          </option>
          {RESULTADOS_DA_LINHA.map((r) => (
            <option key={r} value={r}>
              {t.resultados[r]}
            </option>
          ))}
        </Select>
      </Field>
      <Fundamento id="painel-linha-fundamento" valor={fundamento} onChange={setFundamento} />
      <Falta falta={oQueFalta} estado={gravacao.estado} testId="painel-linha-falta" />
      <BotaoSalvar
        estado={gravacao.estado}
        recusa={gravacao.recusa}
        onSalvar={salvar}
        testId="painel-linha-salvar"
      />
    </div>
  );
}
